/**
 * useSellerDetails Hook
 *
 * Custom hook for fetching and caching seller details from organization and entity.
 * Extracts GSTIN, address, and other seller information needed for invoices.
 */

import { useState, useEffect, useCallback, useRef } from 'react'

import { getOrganization, getEntity } from '../services'

import type { SellerDetails, UseSellerDetailsResult } from '../types/seller'

// Simple in-memory cache with TTL
interface CacheEntry {
  data: SellerDetails
  timestamp: number
}

const CACHE_TTL = 5 * 60 * 1000 // 5 minutes
const sellerDetailsCache = new Map<string, CacheEntry>()

function formatAddress(addr: { line1: string; line2?: string; city: string; postalCode: string }): string {
  return `${addr.line1}${addr.line2 ? `, ${addr.line2}` : ''}, ${addr.city} - ${addr.postalCode}`
}

async function fetchSellerDetailsWithEntity(
  organizationId: string,
  ledgerId: string | undefined,
  entityId: string,
  orgName: string
): Promise<SellerDetails> {
  const entityResponse = await getEntity(entityId)
  if (!entityResponse.data) {
    throw new Error('Failed to load entity details')
  }
  const entity = entityResponse.data
  const primaryAddress = entity.addresses?.find((addr) => addr.isPrimary) ?? entity.addresses?.[0]

  return {
    orgId: organizationId,
    ledgerId,
    name: entity.name?.trim() ? entity.name : orgName,
    gstin: primaryAddress?.gstin ?? '',
    address: primaryAddress ? formatAddress(primaryAddress) : '',
    state: primaryAddress?.state ?? '',
    stateCode: primaryAddress?.stateCode ?? '',
    entityAddresses: entity.addresses ?? [],
  }
}

/**
 * Fetch seller details from the API (extracted to reduce callback complexity).
 */
async function fetchSellerDetailsFromApi(
  organizationId: string,
  ledgerId: string | undefined
): Promise<SellerDetails> {
  const orgResponse = await getOrganization(organizationId)
  if (!orgResponse.data) {
    throw new Error('Failed to load organization')
  }
  const org = orgResponse.data

  if (org.entityId) {
    return fetchSellerDetailsWithEntity(organizationId, ledgerId, org.entityId, org.name)
  }

  return { orgId: organizationId, ledgerId, name: org.name, gstin: '', address: '', state: '', stateCode: '' }
}

type SellerDetailsSetters = {
  setSellerDetails: (v: SellerDetails | null) => void
  setIsLoading: (v: boolean) => void
  setError: (v: Error | null) => void
  isMountedRef: React.RefObject<boolean>
}

async function loadSellerDetails(
  organizationId: string | undefined,
  ledgerId: string | undefined,
  setters: SellerDetailsSetters
): Promise<void> {
  const { setSellerDetails, setIsLoading, setError, isMountedRef } = setters

  if (!organizationId) { setSellerDetails(null); return }

  const cacheKey = `${organizationId}-${ledgerId ?? 'default'}`
  const cached = sellerDetailsCache.get(cacheKey)
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    setSellerDetails(cached.data)
    setIsLoading(false)
    setError(null)
    return
  }

  setIsLoading(true)
  setError(null)

  try {
    const details = await fetchSellerDetailsFromApi(organizationId, ledgerId)
    sellerDetailsCache.set(cacheKey, { data: details, timestamp: Date.now() })
    if (isMountedRef.current) { setSellerDetails(details); setIsLoading(false) }
  } catch (err) {
    const error = err instanceof Error ? err : new Error('Failed to fetch seller details')
    if (isMountedRef.current) { setError(error); setIsLoading(false) }
  }
}

/**
 * Get seller details from organization and entity
 */
export function useSellerDetails(
  organizationId: string | undefined,
  ledgerId: string | undefined
): UseSellerDetailsResult {
  const [sellerDetails, setSellerDetails] = useState<SellerDetails | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => { isMountedRef.current = false }
  }, [])

  const fetchSellerDetails = useCallback(
    () => loadSellerDetails(organizationId, ledgerId, { setSellerDetails, setIsLoading, setError, isMountedRef }),
    [organizationId, ledgerId]
  )

  useEffect(() => { void fetchSellerDetails() }, [fetchSellerDetails])

  return { sellerDetails, isLoading, error, refetch: fetchSellerDetails }
}

/**
 * Clear the seller details cache (useful for testing or manual cache invalidation)
 */
export function clearSellerDetailsCache(): void {
  sellerDetailsCache.clear()
}
